import moment from "moment";

export function assignmentPeriod(assignment) {
  const start = moment(`${assignment?.startDatetime}T${assignment?.startTime}`, "YYYY-MM-DDTHH:mm", true);
  const end = moment(`${assignment?.endDatetime}T${assignment?.endTime}`, "YYYY-MM-DDTHH:mm", true);
  return start.isValid() && end.isValid() && end.isAfter(start) ? { start, end } : null;
}

export function calculateTurnPassengers(assignment) {
  const go = assignment?.vbKeyGo || [];
  const back = assignment?.vbKeyBack || [];
  const goPassengers = go.reduce((sum, item) => sum + (Number(item?.totalPassenger) || 0), 0);
  const backPassengers = back.reduce((sum, item) => sum + (Number(item?.totalPassenger) || 0), 0);
  const turnKey = Number(assignment?.turnKey);
  let requiredCapacity = 0;
  if (turnKey === 1) {
    requiredCapacity = Math.max(goPassengers, backPassengers);
  } else if (turnKey === 2) {
    requiredCapacity = goPassengers;
  } else if (turnKey === 3) {
    requiredCapacity = backPassengers;
  }
  return { goPassengers, backPassengers, requiredCapacity };
}

export function assignmentResourceOptions(data, assignment, siblings = [], index = -1, requiredCapacity) {
  const effectiveCapacity = typeof requiredCapacity === "number"
    ? requiredCapacity
    : calculateTurnPassengers(assignment).requiredCapacity;
  const period = assignmentPeriod(assignment);
  const overlapping = period ? siblings.filter((sibling, siblingIndex) => {
    if (siblingIndex === index) return false;
    const other = assignmentPeriod(sibling);
    return other && period.start.isBefore(other.end) && period.end.isAfter(other.start);
  }) : [];

  const rawVehicles = data?.vehicles || [];
  const eligibleVehicles = rawVehicles.filter(vehicle => {
    const capacity = Number(vehicle.capacity ?? vehicle.Capacity);
    const hasCapacity = Number.isFinite(capacity) && capacity > 0;
    if (effectiveCapacity > 0 && hasCapacity && capacity < effectiveCapacity) {
      return false;
    }
    return true;
  });

  return {
    vehicles: eligibleVehicles.map(vehicle => {
      const localConflict = overlapping.some(other => Number(other.vehicleKey) === vehicle.vehicleKey);
      const capacity = Number(vehicle.capacity ?? vehicle.Capacity);
      const hasCapacity = Number.isFinite(capacity) && capacity > 0;

      let reason = "";
      if (!vehicle.isAvailable) {
        reason = vehicle.reason || "ไม่ว่างในช่วงเวลานี้";
      } else if (localConflict) {
        reason = "ถูกเลือกในเที่ยวอื่นที่ทับซ้อนกัน";
      }

      const name = vehicle.name || `รถ ${vehicle.vehicleKey}`;
      const plate = vehicle.licensePlate ? ` ทะเบียน ${vehicle.licensePlate}` : "";
      const capacityTag = hasCapacity ? ` [${capacity} ที่นั่ง]` : " [ไม่ระบุที่นั่ง]";

      return {
        value: vehicle.vehicleKey,
        label: `${name}${plate}${capacityTag}${reason ? ` — ${reason}` : ""}`,
        isDisabled: !vehicle.isAvailable || localConflict,
        capacity: hasCapacity ? capacity : null,
        reason
      };
    }),
    drivers: (data?.drivers || [])
      .slice()
      .sort((a, b) => {
        const ordA = a.sortOrder && a.sortOrder > 0 ? a.sortOrder : 999999;
        const ordB = b.sortOrder && b.sortOrder > 0 ? b.sortOrder : 999999;
        if (ordA !== ordB) return ordA - ordB;
        return (a.fullName || a.userName || "").localeCompare(b.fullName || b.userName || "");
      })
      .map(driver => {
        const localConflict = overlapping.some(other => other.driverUserName?.toLowerCase() === driver.userName.toLowerCase());
        const reason = !driver.isAvailable ? driver.reason || "ไม่ว่างในช่วงเวลานี้" : localConflict ? "ถูกเลือกในเที่ยวอื่นที่ทับซ้อนกัน" : "";
        const orderPrefix = driver.sortOrder && driver.sortOrder > 0 ? `${driver.sortOrder}. ` : "";
        return {
          value: driver.userName,
          label: `${orderPrefix}${driver.fullName || driver.userName} (${driver.userName})${reason ? ` — ${reason}` : ""}`,
          isDisabled: !driver.isAvailable || localConflict,
          sortOrder: driver.sortOrder || 0
        };
      })
  };
}

export function assertAssignmentResources(assignment, options, ready, requiredCapacity) {
  if (!ready) throw new Error("กรุณารอผลตรวจสอบรถและผู้ขับในช่วงเวลาที่เลือก");
  const effectiveCapacity = typeof requiredCapacity === "number"
    ? requiredCapacity
    : calculateTurnPassengers(assignment).requiredCapacity;
  const vehicle = options.vehicles.find(option => option.value === Number(assignment.vehicleKey));
  const driver = options.drivers.find(option => option.value?.toLowerCase() === assignment.driverUserName?.trim().toLowerCase());
  if (!vehicle || vehicle.isDisabled) {
    if (effectiveCapacity > 0 && (!vehicle || (vehicle.capacity && vehicle.capacity < effectiveCapacity))) {
      throw new Error(`กรุณาเลือกรถที่มีที่นั่งรองรับผู้โดยสาร ${effectiveCapacity} คน`);
    }
    throw new Error("กรุณาเลือกรถที่ว่างในช่วงเวลานี้");
  }
  if (!driver || driver.isDisabled) throw new Error("กรุณาเลือกผู้ขับที่ว่างในช่วงเวลานี้");
}

export function buildVehicleAssignmentPayload(assignments, bookings, { requireComplete = true } = {}) {
  if (!assignments.length || !bookings.length) throw new Error("กรุณาเลือกรายการขอใช้รถและเพิ่มเที่ยวรถ");
  const counts = new Map(bookings.map(booking => [booking.vbid, { go: 0, back: 0, isOneWay: Number(booking.isOneWay) === 1, label: booking.endPlace || booking.label }]));
  const periods = [];
  const payload = assignments.map((assignment, index) => {
    const prefix = `เที่ยวที่ ${index + 1}: `;
    const turnKey = Number(assignment.turnKey);
    const vehicleKey = Number(assignment.vehicleKey);
    const driverUserName = assignment.driverUserName?.trim();
    if (![1, 2, 3].includes(turnKey) || !Number.isInteger(vehicleKey) || vehicleKey <= 0) {
      throw new Error(prefix + "กรุณาเลือกประเภทเที่ยวและรถ");
    }
    if (!driverUserName) throw new Error(prefix + "กรุณาเลือกผู้ขับรถ");
    const start = moment(`${assignment.startDatetime}T${assignment.startTime}`, "YYYY-MM-DDTHH:mm", true);
    const end = moment(`${assignment.endDatetime}T${assignment.endTime}`, "YYYY-MM-DDTHH:mm", true);
    if (!start.isValid() || !end.isValid()) throw new Error(prefix + "กรุณาระบุวันที่และเวลาให้ครบถ้วน");
    if (!end.isAfter(start)) throw new Error(prefix + "เวลาสิ้นสุดต้องอยู่หลังเวลาเริ่มต้น");
    const go = assignment.vbKeyGo || [];
    const back = assignment.vbKeyBack || [];
    if ((turnKey === 1 && (!go.length || !back.length)) ||
        (turnKey === 2 && (!go.length || back.length)) ||
        (turnKey === 3 && (!back.length || go.length))) {
      throw new Error(prefix + "กรุณาเลือกคำขอขาไป/ขากลับให้ตรงกับประเภทเที่ยว");
    }
    const links = (rows, direction) => rows.map(row => {
      const count = counts.get(row.vbid);
      if (!count) throw new Error(prefix + "พบคำขอที่ไม่ได้เลือกไว้");
      if (direction === "back" && count.isOneWay) throw new Error(`${count.label}: เที่ยวเดียว จัดได้เฉพาะขาไป`);
      count[direction]++;
      if (count[direction] > 1) throw new Error(`${count.label}: เลือก${direction === "go" ? "ขาไป" : "ขากลับ"}ซ้ำ`);
      return { vbid: row.vbid };
    });
    if (periods.some(period => period.vehicleKey === vehicleKey && start.isBefore(period.end) && end.isAfter(period.start))) {
      throw new Error("รถคันเดียวกันมีช่วงเวลาเที่ยวรถซ้อนกัน");
    }
    if (periods.some(period => period.driverUserName.toLowerCase() === driverUserName.toLowerCase() && start.isBefore(period.end) && end.isAfter(period.start))) {
      throw new Error("ผู้ขับรถคนเดียวกันมีช่วงเวลาเที่ยวรถซ้อนกัน");
    }
    periods.push({ vehicleKey, driverUserName, start, end });
    return {
      startDatetime: assignment.startDatetime, startTime: assignment.startTime,
      endDatetime: assignment.endDatetime, endTime: assignment.endTime,
      turnKey, vehicleKey, driverUserName, vbKeyGo: links(go, "go"), vbKeyBack: links(back, "back")
    };
  });
  if (requireComplete) counts.forEach(count => {
    if (count.go !== 1 || count.back !== (count.isOneWay ? 0 : 1))
      throw new Error(`${count.label}: ${count.isOneWay ? "เที่ยวเดียว ต้องจัดขาไปหนึ่งเที่ยว" : "ต้องจัดขาไปและขากลับอย่างละหนึ่งเที่ยว"}`);
  });
  return payload;
}
